import { ButtonType, ButtonWidget, DialogType, DialogWidget, ProgressBarWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';

import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { Messages } from '../../codegen/Localize';
import { CreateCustomDialogFn, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { UploadContentInProgressAction, UploadContentProgressActionsTypes } from '../actions/UploadContentProgressActions';
import { UploadContentProgressDialogStyles } from '../styles/UploadContentProgressDialogStyles';
import { formatBytes } from '../utils/UploadContentDialogHelper';

export type UploadProgressDialogStateProps = Pick<UploadContentInProgressAction, 'bytesUploaded' | 'totalBytes'> & { uploadStatus: string };
export type UploadProgressDialogOwnProps = {
  id: string;
  totalBytes: number;
  filename: string;
  publishingMessage: string;
  close: () => void;
  cancelRequest: () => void;
};

export class UploadProgressDialog extends React.PureComponent<UploadProgressDialogStateProps & UploadProgressDialogOwnProps> {

  private formattedTotalBytes: string;

  constructor(props: UploadProgressDialogStateProps & UploadProgressDialogOwnProps) {
    super(props);
    this.formattedTotalBytes = formatBytes(this.props.totalBytes);
  }

  private getFormattedUploadStatus(): string {
    const sent = formatBytes(this.props.bytesUploaded);
    return Messages.contentUpload_uploadContentNameProgressFileStatus({
      SENT: sent,
      TOTAL: this.formattedTotalBytes
    });
  }

  private getPercentage(): number {
    if (this.props.uploadStatus === UploadContentProgressActionsTypes.COMPLETE) {
      return 100;
    }
    const percentageAsInteger = (this.props.bytesUploaded / this.props.totalBytes) * 100;
    return (Math.ceil(percentageAsInteger) / 100) * 90;  // We want to save the last 10% for publish step
  }

  private isPublishing(): boolean {
    return this.props.bytesUploaded === this.props.totalBytes;
  }

  private handleCancel = () => {
    this.props.cancelRequest();
    this.props.close();
  };

  private unloadHandler = (event: any) => {
    event.preventDefault();
    event.returnValue = true;
  };

  componentDidMount = () => {
    window.addEventListener('beforeunload', this.unloadHandler);
  };

  componentWillUnmount(): void {
    window.removeEventListener('beforeunload', this.unloadHandler);
  }

  render() {

    return (

      <div className={UploadContentProgressDialogStyles.contentContainer}>
        <h2 className={UploadContentProgressDialogStyles.title}>{Messages.contentUpload_uploadContentNameProgressTitle()}</h2>
        <ProgressBarWidget
          label={<span style={{ overflowWrap: 'anywhere' }}>{this.props.filename}</span>}
          className={UploadContentProgressDialogStyles.progressContainer}
          value={this.getPercentage()}
          max={100}
          message={!this.isPublishing() ? this.getFormattedUploadStatus() : this.props.publishingMessage}
        />
        <ButtonWidget
          buttonType={ButtonType.Outline}
          testId="upload-content-progress-cancel"
          children={Messages.cancel()}
          handleClick={this.handleCancel}
          disabled={this.isPublishing()}
        />
      </div>

    );
  }
}

const mapStateToProps: MapStateToProps<UploadProgressDialogStateProps, UploadProgressDialogOwnProps> = (state: AppState, ownProps) => {
  const progress = state.uploadContent.uploads.get(ownProps.id);
  return {
    bytesUploaded: progress?.bytesUploaded || 0,
    totalBytes: progress?.totalBytes || ownProps.totalBytes,
    uploadStatus: progress?.type || UploadContentProgressActionsTypes.IN_PROGRESS,
  };
};

const ConnectedUploadProgressDialog = connect(mapStateToProps)(UploadProgressDialog);

/**
 * Options used for ReactModalService
 */
export function UploadProgressDialogOpts(id: string, totalBytes: number, filename: string, publishingMessage: string, cancelRequest: () => void): CreateCustomDialogFn {
  return (closeAndDismiss) => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.Uber,
      showCloseButton: false,
      dialogWidth: 400,
      shouldNotCloseOnGlassClick: true,
      disableAutoFocus: true,
      testId: 'upload-content-progress'
    }, closeAndDismiss);

    return (
      <DialogWidget {...dialogProps}>
        <ConnectedUploadProgressDialog
          id={id}
          totalBytes={totalBytes}
          filename={filename}
          publishingMessage={publishingMessage}
          cancelRequest={cancelRequest}
          close={closeAndDismiss.close}
        />
      </DialogWidget>
    );
  };
}
